import { memo, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import type { PhotoProviderProps } from 'react-photo-view/dist/PhotoProvider';
import type { PhotoRenderParams } from 'react-photo-view/dist/types';
import 'react-photo-view/dist/react-photo-view.css';

import type { MediaItem } from './types';

const DEFAULT_VIDEO_SIZE = { width: 720, height: 405 } as const;

const getViewportSize = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_VIDEO_SIZE;
  }

  return {
    width: window.innerWidth || DEFAULT_VIDEO_SIZE.width,
    height: window.innerHeight || DEFAULT_VIDEO_SIZE.height,
  };
};

const getRenderBoxSize = (sourceWidth: number, sourceHeight: number) => {
  if (sourceWidth <= 0 || sourceHeight <= 0) {
    return DEFAULT_VIDEO_SIZE;
  }

  const viewport = getViewportSize();
  const safeContainerWidth = viewport.width > 0 ? viewport.width : sourceWidth;
  const safeContainerHeight = viewport.height > 0 ? viewport.height : sourceHeight;
  const scale = Math.min(safeContainerWidth / sourceWidth, safeContainerHeight / sourceHeight, 1);

  return {
    width: Math.max(Math.round(sourceWidth * scale), 1),
    height: Math.max(Math.round(sourceHeight * scale), 1),
  };
};

export interface MediaViewerProps
  extends Pick<
    PhotoProviderProps,
    | 'loop'
    | 'speed'
    | 'easing'
    | 'photoClosable'
    | 'maskClosable'
    | 'maskOpacity'
    | 'pullClosable'
    | 'bannerVisible'
    | 'overlayRender'
    | 'toolbarRender'
    | 'portalContainer'
    | 'className'
    | 'maskClassName'
    | 'photoWrapClassName'
    | 'photoClassName'
    | 'loadingElement'
    | 'brokenElement'
  > {
  items: MediaItem[];
  /** 缩略图尺寸，单位 px */
  thumbSize?: number;
  /** 缩略图间距，单位 px */
  gap?: number;
  /** 最多展示多少个缩略图（仍可在预览层切换全部 items）；不传则不限制 */
  maxThumbs?: number;
  /**
   * 自定义视频预览层渲染（放大后的内容）
   * - 默认：<video controls playsInline />
   * - 业务需要时可注入自研播放器/平台特殊逻辑
   */
  videoRender?: (item: MediaItem) => React.ReactNode;
  /**
   * 自定义触发元素，传入后将替代默认缩略图列表
   * - 点击 trigger 会打开整组预览（从第一张开始）
   * - 仍可在预览层内切换全部 items
   */
  trigger?: React.ReactNode;
}

/**
 * 媒体查看器（图片/视频混合）
 *
 * 最小用法示例：
 *
 * ```tsx
 * import { MediaViewer, MediaItem } from 'components/MediaViewer';
 *
 * const items: MediaItem[] = [
 *   { type: 'image', src: 'https://xx/a.jpg', thumb: 'https://xx/a_thumb.jpg' },
 *   { type: 'video', src: 'https://xx/a.mp4', thumb: 'https://xx/a_poster.jpg' },
 * ];
 *
 * <MediaViewer items={items} maxThumbs={6} />
 * ```
 *
 * 自定义视频放大层渲染（需要接入业务播放器时）：
 *
 * ```tsx
 * <MediaViewer
 *   items={items}
 *   videoRender={(item) => <MyVideoPlayer url={item.src} />}
 * />
 * ```
 */
function MediaViewer(props: MediaViewerProps) {
  const {
    items,
    thumbSize = 60,
    gap = 8,
    maxThumbs,
    maskOpacity = 0.5,
    videoRender,
    trigger,
    ...providerProps
  } = props;

  const normalized = useMemo(() => {
    return (items || []).filter((x) => x && x.src);
  }, [items]);

  const displayCount = useMemo(() => {
    if (!maxThumbs || maxThumbs <= 0) return normalized.length;
    return Math.min(maxThumbs, normalized.length);
  }, [maxThumbs, normalized.length]);

  const restCount = normalized.length - displayCount;

  // 预拉取视频 metadata（用于按真实比例自适应满屏展示）
  const [videoMeta, setVideoMeta] = useState<Record<string, { width: number; height: number }>>({});

  useEffect(() => {
    const videos = normalized.filter((x) => x.type === 'video').map((x) => x.src);
    if (videos.length === 0) return;

    let cancelled = false;
    const controllers: AbortController[] = [];

    videos.forEach((src) => {
      if (!src || videoMeta[src]) return;

      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;

      // 某些浏览器支持 abort；不支持也没关系
      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      if (controller) controllers.push(controller);

      const cleanup = () => {
        video.onloadedmetadata = null;
        video.onerror = null;
        // 释放资源
        try {
          video.removeAttribute('src');
          video.load();
        } catch {}
      };

      video.onloadedmetadata = () => {
        if (cancelled) return cleanup();
        const width = video.videoWidth || 0;
        const height = video.videoHeight || 0;
        if (width > 0 && height > 0) {
          setVideoMeta((prev) => {
            if (prev[src]) return prev;
            return { ...prev, [src]: { width, height } };
          });
        }
        cleanup();
      };

      video.onerror = () => {
        cleanup();
      };

      try {
        video.src = src;
      } catch {
        cleanup();
      }
    });

    return () => {
      cancelled = true;
      controllers.forEach((c) => c.abort());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalized, videoMeta]);

  const defaultVideoRender = (item: MediaItem) => {
    return <PreviewVideoPlayer src={item.src} poster={item.poster} />;
  };

  const renderWithGalleryAttrs = (
    params: PhotoRenderParams,
    node: React.ReactNode,
    elementWidth: number,
    elementHeight: number,
  ) => {
    const { attrs } = params;

    return (
      <RenderRoot {...(attrs as any)}>
        <RenderInner
          style={{
            width: elementWidth,
            height: elementHeight,
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          {node}
        </RenderInner>
      </RenderRoot>
    );
  };

  if (normalized.length === 0) return null;

  const renderPhotoView = (item: MediaItem, idx: number, childOverride?: React.ReactNode) => {
    const baseKey = item.key ?? `${item.type}:${item.src}`;
    const child = childOverride ?? <HiddenAnchor />;

    if (item.type === 'image') {
      return (
        <PhotoView key={baseKey} src={item.src}>
          {child}
        </PhotoView>
      );
    }

    const meta = videoMeta[item.src];
    const originVideoSize = meta ?? DEFAULT_VIDEO_SIZE;
    const videoSize = getRenderBoxSize(originVideoSize.width, originVideoSize.height);
    // 关键：react-photo-view 可能只在首次挂载时读取 width/height，因此异步 metadata 需要触发重新挂载
    const photoViewKey = `${baseKey}:${videoSize.width}x${videoSize.height}`;

    return (
      <PhotoView
        key={photoViewKey}
        width={item.type === 'video' ? videoSize.width : undefined}
        height={item.type === 'video' ? videoSize.height : undefined}
        render={(params) => {
          const node = (videoRender || defaultVideoRender)(item);
          return renderWithGalleryAttrs(params, node, videoSize.width, videoSize.height);
        }}
      >
        {child}
      </PhotoView>
    );
  };

  if (trigger) {
    return (
      <PhotoProvider maskOpacity={maskOpacity} {...providerProps}>
        {normalized.map((item, idx) => renderPhotoView(item, idx, idx === 0 ? trigger : undefined))}
      </PhotoProvider>
    );
  }

  return (
    <PhotoProvider maskOpacity={maskOpacity} {...providerProps}>
      <ThumbList
        style={{ ['--thumb-size' as any]: `${thumbSize}px`, ['--gap' as any]: `${gap}px` }}
      >
        {normalized.map((item, idx) => {
          const baseKey = item.key ?? `${item.type}:${item.src}`;
          const isVisibleThumb = idx < displayCount;
          const isLastVisible = isVisibleThumb && idx === displayCount - 1;
          const showMoreMask = isLastVisible && restCount > 0;

          const thumbNode =
            item.type === 'image' ? (
              <ThumbImage src={item.thumb || item.src} alt="" loading="lazy" />
            ) : item.thumb ? (
              <ThumbImage src={item.thumb} alt="" loading="lazy" />
            ) : (
              <ThumbVideo src={item.src} muted playsInline preload="metadata" />
            );

          const child = isVisibleThumb ? (
            <ThumbItem>
              {thumbNode}
              {item.type === 'video' && <PlayBadge>▶</PlayBadge>}
              {showMoreMask && <MoreMask>+{restCount}</MoreMask>}
            </ThumbItem>
          ) : (
            // 仍然渲染一个隐藏节点，让 react-photo-view 感知整组 items，从而支持组内切换
            <HiddenAnchor />
          );

          if (item.type === 'image') {
            return (
              <PhotoView key={baseKey} src={item.src}>
                {child}
              </PhotoView>
            );
          }

          const meta = videoMeta[item.src];
          const originVideoSize = meta ?? DEFAULT_VIDEO_SIZE;
          const videoSize = getRenderBoxSize(originVideoSize.width, originVideoSize.height);
          // 关键：react-photo-view 可能只在首次挂载时读取 width/height，因此异步 metadata 需要触发重新挂载
          const photoViewKey = `${baseKey}:${videoSize.width}x${videoSize.height}`;

          return (
            <PhotoView
              key={photoViewKey}
              width={item.type === 'video' ? videoSize.width : undefined}
              height={item.type === 'video' ? videoSize.height : undefined}
              render={(params) => {
                const node = (videoRender || defaultVideoRender)(item);
                return renderWithGalleryAttrs(params, node, videoSize.width, videoSize.height);
              }}
            >
              {child}
            </PhotoView>
          );
        })}
      </ThumbList>
    </PhotoProvider>
  );
}

export default memo(MediaViewer);

interface PreviewVideoPlayerProps {
  src: string;
  poster?: string;
}

const PreviewVideoPlayer = memo(function PreviewVideoPlayer(props: PreviewVideoPlayerProps) {
  const { src, poster } = props;
  const [isReady, setIsReady] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setIsReady(false);
    setHasError(false);
  }, [src]);

  const handleReady = () => {
    setIsReady(true);
    setHasError(false);
  };

  const handleError = () => {
    setHasError(true);
    setIsReady(true);
  };

  return (
    <VideoPlayerShell>
      {!isReady && !hasError && (
        <VideoLoadingOverlay>
          <VideoLoadingSpinner />
        </VideoLoadingOverlay>
      )}
      <VideoPlayer
        src={src}
        poster={poster}
        controls
        playsInline
        preload="metadata"
        onLoadedData={handleReady}
        onCanPlay={handleReady}
        onPlaying={handleReady}
        onError={handleError}
      />
    </VideoPlayerShell>
  );
});

const ThumbList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--gap);
`;

const ThumbItem = styled.div`
  width: var(--thumb-size);
  height: var(--thumb-size);
  border-radius: 4px;
  overflow: hidden;
  background: #f3f3f3;
  position: relative;
  cursor: pointer;

  > img,
  > video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const ThumbImage = styled.img``;

const ThumbVideo = styled.video``;

const PlayBadge = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 18px;
  background: rgba(0, 0, 0, 0.15);
  pointer-events: none;
`;

const MoreMask = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  background: rgba(0, 0, 0, 0.55);
  pointer-events: none;
`;

const HiddenAnchor = styled.span`
  width: 0;
  height: 0;
  overflow: hidden;
  position: absolute;
  pointer-events: none;
  opacity: 0;
`;

const RenderRoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const RenderInner = styled.div`
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  flex-shrink: 0;
`;

const VideoPlayerShell = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background: #000;
`;

const VideoLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  pointer-events: none;
  z-index: 1;
`;

const VideoLoadingSpinner = styled.span`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.25);
  border-top-color: #fff;
  animation: media-viewer-spin 0.8s linear infinite;

  @keyframes media-viewer-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const VideoPlayer = styled.video`
  max-width: 100%;
  max-height: 100%;
  background: #000;
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
`;
